import { useState } from 'react';
import { useInView } from '@/hooks/useInView';

const propertyTypes = [
  {
    id: 'long-term',
    label: 'Long-Term Residential',
    tagline: 'Stability. Consistency. Peace of Mind.',
    description:
      'Our long-term residential management service is built for owners who value sustained performance and tenant quality above all else. We handle every facet of the tenancy lifecycle — from rigorous screening to lease renewals — ensuring your property generates reliable income with minimal friction.',
    image:
      'https://public.readdy.ai/ai/img_res/9f4e569cd2a6e0482776f1aaf1c8d825.jpg',
    differentiators: [
      { icon: 'ri-user-search-line', label: 'Multi-Layer Tenant Vetting' },
      { icon: 'ri-file-text-line', label: 'Full Lease Lifecycle Management' },
      { icon: 'ri-bar-chart-line', label: 'Monthly Performance Reporting' },
      { icon: 'ri-shield-check-line', label: 'Legal Compliance Oversight' },
      { icon: 'ri-customer-service-2-line', label: 'Dedicated Relationship Manager' },
      { icon: 'ri-money-dollar-circle-line', label: 'Rent Collection &amp; Disbursement' },
    ],
  },
  {
    id: 'short-term',
    label: 'Short-Term Rentals',
    tagline: 'Maximised Returns. Effortless Execution.',
    description:
      'Short-term rental management demands agility, precision, and an exceptional guest experience. We manage every touchpoint — from dynamic pricing strategy to seamless guest communication and property turnover — so your asset performs at its peak without demanding your time.',
    image:
      'https://readdy.ai/api/search-image?query=modern-luxury-short-term-rental-apartment-beautifully-staged-contemporary-design-stylish-bedroom-premium-linens-curated-decor-sophisticated-minimalist-aesthetic-high-end-hospitality-feel-warm-inviting-atmosphere-upscale-temporary-residence&width=900&height=700&seq=pt-shortterm01&orientation=portrait',
    differentiators: [
      { icon: 'ri-price-tag-3-line', label: 'Dynamic Pricing Optimisation' },
      { icon: 'ri-calendar-check-line', label: 'Booking &amp; Calendar Management' },
      { icon: 'ri-star-line', label: 'Guest Experience Curation' },
      { icon: 'ri-refresh-line', label: 'Professional Turnover Service' },
      { icon: 'ri-camera-line', label: 'Premium Listing Photography' },
      { icon: 'ri-line-chart-line', label: 'Revenue Analytics &amp; Reporting' },
    ],
  },
  {
    id: 'vacation',
    label: 'Vacation Properties',
    tagline: 'Luxury Preserved. Legacy Protected.',
    description:
      "Vacation properties require a level of care that goes beyond standard management. We treat your retreat as the singular asset it is — maintaining it to the highest standard year-round, coordinating seasonal preparation, and ensuring every guest experience reflects the property's true character.",
    image:
      'https://readdy.ai/api/search-image?query=breathtaking-luxury-vacation-villa-infinity-pool-overlooking-ocean-tropical-landscape-elegant-outdoor-living-space-premium-resort-style-architecture-lush-surroundings-sophisticated-holiday-retreat-exclusive-private-estate-warm-golden-light&width=900&height=700&seq=pt-vacation01&orientation=portrait',
    differentiators: [
      { icon: 'ri-home-heart-line', label: 'White-Glove Property Care' },
      { icon: 'ri-sun-line', label: 'Seasonal Preparation &amp; Winterisation' },
      { icon: 'ri-vip-crown-line', label: 'Concierge Guest Services' },
      { icon: 'ri-tools-line', label: 'Trusted Maintenance Network' },
      { icon: 'ri-lock-password-line', label: 'Secure Access Management' },
      { icon: 'ri-eye-line', label: 'Year-Round Property Monitoring' },
    ],
  },
];

export default function PropertyTypes() {
  const [active, setActive] = useState(0);
  const [panelVisible, setPanelVisible] = useState(true);

  const [headerRef, headerInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [tabsRef, tabsInView] = useInView<HTMLDivElement>({ threshold: 0.3 });
  const [panelRef, panelInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  const current = propertyTypes[active];

  const handleTabChange = (index: number) => {
    if (index === active) return;
    setPanelVisible(false);
    setTimeout(() => {
      setActive(index);
      setPanelVisible(true);
    }, 220);
  };

  return (
    <section id="property-types" className="w-full bg-[#FAF9F7] py-28 px-6 md:px-16">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row gap-12 md:gap-24 mb-16 transition-all duration-700 ease-out ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="md:w-1/3">
            <span className="text-xs uppercase tracking-[0.25em] text-[#8B7355] font-semibold">
              — Property Types
            </span>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] leading-tight mb-4">
              Three Tiers of Managed Excellence
            </h2>
            <p className="text-[#5A5A5A] text-base md:text-lg font-light leading-relaxed">
              Each property type demands a distinct approach. We bring specialised expertise to every tier, ensuring your asset is managed with the precision it deserves.
            </p>
          </div>
        </div>

        {/* Tab Selectors */}
        <div
          ref={tabsRef}
          className={`flex flex-col sm:flex-row gap-0 mb-12 border border-[#E0DDD9] rounded-lg overflow-hidden transition-all duration-700 ease-out ${
            tabsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: tabsInView ? '150ms' : '0ms' }}
        >
          {propertyTypes.map((type, index) => (
            <button
              key={type.id}
              onClick={() => handleTabChange(index)}
              className={`flex-1 px-6 py-4 text-sm font-medium tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap text-center ${
                active === index
                  ? 'bg-[#2C2C2C] text-white'
                  : 'bg-white text-[#5A5A5A] hover:bg-[#F5F4F2] hover:text-[#2C2C2C]'
              } ${index < propertyTypes.length - 1 ? 'border-b sm:border-b-0 sm:border-r border-[#E0DDD9]' : ''}`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Content Panel */}
        <div
          ref={panelRef}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-lg overflow-hidden border border-[#E0DDD9] transition-all duration-700 ease-out ${
            panelInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ transitionDelay: panelInView ? '250ms' : '0ms' }}
        >
          {/* Image */}
          <div className="relative h-[340px] lg:h-auto min-h-[420px] overflow-hidden">
            <img
              key={current.id}
              src={current.image}
              alt={current.label}
              className={`w-full h-full object-cover object-top transition-all duration-500 ease-out ${
                panelVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
            <div className="absolute bottom-6 left-6">
              <span
                className={`inline-block bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-all duration-400 ${
                  panelVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                }`}
              >
                {current.label}
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="bg-white p-10 md:p-12 flex flex-col justify-between">
            <div
              className={`transition-all duration-400 ease-out ${
                panelVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              <p className="text-xs uppercase tracking-[0.25em] text-[#8B7355] font-semibold mb-4">
                {current.tagline}
              </p>
              <p className="text-[#4A4A4A] text-base leading-relaxed font-light mb-10">
                {current.description}
              </p>

              {/* Differentiators — staggered */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {current.differentiators.map((item, i) => (
                  <div
                    key={item.label}
                    className={`flex items-center gap-3 transition-all duration-400 ease-out`}
                    style={{
                      transitionDelay: panelVisible ? `${i * 60 + 80}ms` : '0ms',
                      opacity: panelVisible ? 1 : 0,
                      transform: panelVisible ? 'translateY(0)' : 'translateY(8px)',
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F4F2] flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#8B7355]`}></i>
                    </div>
                    <span
                      className="text-sm text-[#3A3A3A] font-light"
                      dangerouslySetInnerHTML={{ __html: item.label }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation dots */}
            <div className="flex items-center gap-3 mt-10 pt-8 border-t border-[#F0EDE9]">
              {propertyTypes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleTabChange(i)}
                  className={`transition-all duration-300 cursor-pointer rounded-full ${
                    active === i
                      ? 'w-8 h-2 bg-[#8B7355]'
                      : 'w-2 h-2 bg-[#D0CCC7] hover:bg-[#8B7355]/50'
                  }`}
                  aria-label={`View ${propertyTypes[i].label}`}
                />
              ))}
              <span className="ml-auto text-xs text-[#9A9A9A] font-light tracking-wide">
                {active + 1} / {propertyTypes.length}
              </span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}